import React from 'react'
import ErrorBoundary, { SectionError, isChunkLoadError } from './ErrorBoundary'
import { forgetOpenPanel, restoreWhereAt } from '../context/whereAt'
import { Loading } from '../ui/States'

type Loader = () => Promise<{ default: React.ComponentType }>

// A lazily loaded screen part that can be loaded again: React.lazy keeps a
// failed import for good, so Retry swaps in a fresh one.
export interface Retryable {
  get: () => React.ComponentType
  reset: () => void
}

export function retryableLazy(load: Loader): Retryable {
  let lazy: React.ComponentType = React.lazy(load)
  return {
    get: () => lazy,
    reset: () => {
      lazy = React.lazy(load)
    },
  }
}

// LazySection renders one Settings section. If its code fails to load (or it
// throws), the error stays inside the section with a Retry, the rest of the
// page keeps working, and the app stops reopening this page on launch.
//
// Retry after a failed download reloads the page and comes back here: the
// browser remembers a failed module for the life of the page, so importing
// it again in place would fail the same way (seen in Chrome). Retry after
// any other error just renders the section again.
export default function LazySection({ source, children }: { source?: Retryable; children?: React.ReactNode }) {
  const [attempt, setAttempt] = React.useState(0)
  const forgotten = React.useRef<string | null>(null)
  const Lazy = source?.get()
  return (
    <ErrorBoundary
      resetKey={attempt}
      onError={() => {
        forgotten.current = forgetOpenPanel() ?? forgotten.current
      }}
      fallback={(_retry, error) => (
        <SectionError
          onRetry={() => {
            if (isChunkLoadError(error)) {
              restoreWhereAt(forgotten.current)
              window.location.reload()
              return
            }
            source?.reset()
            setAttempt((n) => n + 1)
          }}
        />
      )}
    >
      <React.Suspense fallback={<Loading />}>
        {Lazy ? <Lazy /> : children}
      </React.Suspense>
    </ErrorBoundary>
  )
}
